import { MedicalProfileFiles } from '../../../primary-record-types';
import { Dimensions, WidthDimensions, FlexibleContainerDimensions, WidthMaxHeight } from './types';

type FitDimensionsToContainerArgs = {
    containerDimensions: FlexibleContainerDimensions;
    dimensions: Dimensions;
    rotation: MedicalProfileFiles.Rotation;
    fileType: 'pdf-image' | 'image';
};

type FitDimensionsToContainerReturnValue = {
    renderDimensions: Dimensions;
    renderRotation: number;
};

const maybeRotateDimensions = (dimensions: Dimensions, rotation: MedicalProfileFiles.Rotation): Dimensions => {
    if (rotation === 90 || rotation === 270) {
        return {
            width: dimensions.height,
            height: dimensions.width,
        };
    }

    return dimensions;
};

const isWidthDimensions = (dimensions: FlexibleContainerDimensions): dimensions is WidthDimensions => {
    return 'height' in dimensions === false;
};

const isWidthMaxHeight = (dimensions: FlexibleContainerDimensions): dimensions is WidthMaxHeight => {
    return 'maxHeight' in dimensions;
};

/**
 * Search results display the whole width but only a portion of the height.  It then highlights the relevant section
 */
export const getSearchResultDisplayBoundingBoxes = (
    searchResultBoundingBox: MedicalProfileFiles.BoundingBox,
): { snippetBoundingBox: MedicalProfileFiles.BoundingBox; overlayBoundingBox: MedicalProfileFiles.BoundingBox } => {
    const snippetBoundingBox: MedicalProfileFiles.BoundingBox = {
        ...searchResultBoundingBox,
        left: 0,
        width: 1,
    };

    const overlayBoundingBox: MedicalProfileFiles.BoundingBox = {
        ...searchResultBoundingBox,
        top: 0,
        height: 1,
    };

    return { snippetBoundingBox, overlayBoundingBox };
};

/**
 * The layout may not be concerned about a heigh restriction, if so, the height will be set based on the content dimensions and the container width
 */
const flexibleToStandardDimensions = (
    dimensions: FlexibleContainerDimensions,
    contentDimensions: Dimensions,
): Dimensions => {
    if (isWidthMaxHeight(dimensions)) {
        return {
            width: dimensions.width,
            height: Math.min(contentDimensions.height, dimensions.maxHeight),
        };
    } else if (isWidthDimensions(dimensions)) {
        return {
            width: dimensions.width,
            height: (contentDimensions.height / contentDimensions.width) * dimensions.width,
        };
    } else {
        return dimensions;
    }
};

export const maybeRotateBoundingBox = (
    boundingBox: MedicalProfileFiles.BoundingBox,
    rotation: MedicalProfileFiles.Rotation,
    imageType: 'pdf-image' | 'image',
): MedicalProfileFiles.BoundingBox => {
    if (imageType === 'image') {
        // textract seems to generate bounding boxes that respect exif orientation
        return boundingBox;
    }

    if (rotation === 90) {
        return {
            width: boundingBox.height,
            height: boundingBox.width,
            left: 1 - boundingBox.top - boundingBox.height,
            top: boundingBox.left,
        };
    }

    if (rotation === 180) {
        return {
            width: boundingBox.width,
            height: boundingBox.height,
            left: 1 - boundingBox.left - boundingBox.width,
            top: 1 - boundingBox.top - boundingBox.height,
        };
    }

    if (rotation === 270) {
        return {
            width: boundingBox.height,
            height: boundingBox.width,
            left: boundingBox.top,
            top: 1 - boundingBox.left - boundingBox.width,
        };
    }

    return boundingBox;
};

export const fitDimensionsToContainer = ({
    containerDimensions,
    dimensions,
    rotation,
}: FitDimensionsToContainerArgs): FitDimensionsToContainerReturnValue => {
    const rotatedDimensions = maybeRotateDimensions(dimensions, rotation);
    const validatedContainerDimensions = flexibleToStandardDimensions(containerDimensions, rotatedDimensions);
    const containerAspectRatio = validatedContainerDimensions.width / validatedContainerDimensions.height;
    const aspectRatio = rotatedDimensions.width / rotatedDimensions.height;

    if (containerAspectRatio > aspectRatio) {
        return {
            renderDimensions: {
                width: validatedContainerDimensions.height * aspectRatio,
                height: validatedContainerDimensions.height,
            },
            renderRotation: rotation,
        };
    }

    return {
        renderDimensions: {
            width: validatedContainerDimensions.width,
            height: validatedContainerDimensions.width / aspectRatio,
        },
        renderRotation: rotation,
    };
};

export type GetPageRenderDimensionsArgs = FitDimensionsToContainerArgs;
export type GetPageRenderDimensionsReturnValue = FitDimensionsToContainerReturnValue;

export const getPageRenderDimensions = (args: GetPageRenderDimensionsArgs): GetPageRenderDimensionsReturnValue => {
    const result = fitDimensionsToContainer(args);
    return result;
};

export type Offset = {
    top: number;
    left: number;
};

export type SnippetRenderDimensions = {
    containerDimensions: Dimensions;
    contentDimensions: Dimensions;
    offset: Offset;
    rotation: MedicalProfileFiles.Rotation;
};

export type GetSnippetRenderDimensionsArgs = GetPageRenderDimensionsArgs & {
    boundingBox: MedicalProfileFiles.BoundingBox;
};

export type GetSnippetRenderDimensionsReturnValue = SnippetRenderDimensions;

export const getSnippetRenderDimensions = (
    args: GetSnippetRenderDimensionsArgs,
): GetSnippetRenderDimensionsReturnValue => {
    const maybeRotatedDimensions = maybeRotateDimensions(args.dimensions, args.rotation);
    const maybeRotatedBoundingBox = maybeRotateBoundingBox(args.boundingBox, args.rotation, args.fileType);

    const originalSnippetDimensions: Dimensions = {
        width: maybeRotatedBoundingBox.width * maybeRotatedDimensions.width,
        height: maybeRotatedBoundingBox.height * maybeRotatedDimensions.height,
    };

    const validatedContainerDimensions = flexibleToStandardDimensions(
        args.containerDimensions,
        originalSnippetDimensions,
    );

    const scaleFactor = Math.min(
        validatedContainerDimensions.width / originalSnippetDimensions.width,
        validatedContainerDimensions.height / originalSnippetDimensions.height,
        1,
    );

    const scaledSnippetDimensions: Dimensions = {
        width: originalSnippetDimensions.width * scaleFactor,
        height: originalSnippetDimensions.height * scaleFactor,
    };

    const scaledContentDimensions: Dimensions = {
        width: maybeRotatedDimensions.width * scaleFactor,
        height: maybeRotatedDimensions.height * scaleFactor,
    };

    const top = maybeRotatedBoundingBox.top * scaledContentDimensions.height;
    const left = maybeRotatedBoundingBox.left * scaledContentDimensions.width;
    const returnValue: GetSnippetRenderDimensionsReturnValue = {
        containerDimensions: scaledSnippetDimensions,
        contentDimensions: scaledContentDimensions,
        offset: { top, left },
        rotation: args.rotation,
    };

    return returnValue;
};
